import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export interface Rule {
  id: number;
  sender: string;
  domain: string;
  subjectPattern: string | null;
  matchType: 'sender' | 'domain' | 'sender+subject' | 'domain+subject';
  originalCategory: string | null;
  newCategory: string;
  appliedCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface RulesResult {
  rows: Rule[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface CreateRuleInput {
  sender?: string;
  domain?: string;
  subjectPattern?: string;
  matchType: 'sender' | 'domain' | 'sender+subject' | 'domain+subject';
  originalCategory?: string;
  newCategory: string;
}

export interface TestRuleInput {
  sender?: string;
  domain?: string;
  subject?: string;
}

export interface TestRuleResult {
  matches: Rule[];
}

export function useRules(page = 1, search = '', category = '') {
  return useQuery<RulesResult>({
    queryKey: ['rules', page, search, category],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), limit: '50' });
      if (search) params.set('search', search);
      if (category) params.set('category', category);
      return api.get<RulesResult>(`/api/rules?${params.toString()}`);
    },
  });
}

export function useRule(id: number) {
  return useQuery<Rule>({
    queryKey: ['rule', id],
    queryFn: () => api.get<Rule>(`/api/rules/${id}`),
    enabled: id > 0,
  });
}

export function useCreateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRuleInput) => api.post<Rule>('/api/rules', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rules'] }),
  });
}

export function useUpdateRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: CreateRuleInput & { id: number }) =>
      api.put<Rule>(`/api/rules/${id}`, body),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['rules'] });
      void qc.invalidateQueries({ queryKey: ['rule', vars.id] });
    },
  });
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ ok: boolean }>(`/api/rules/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rules'] }),
  });
}

export function useTestRule() {
  return useMutation({
    mutationFn: (body: TestRuleInput) => api.post<TestRuleResult>('/api/rules/test', body),
  });
}
